import React, { useCallback } from 'react';
import styled from 'styled-components';
import Flag from 'react-flagkit';

import Button from '../../../Button';
import OrderStatus from '../OrderStatus';
import {
  ArrowUpRight,
  AttachTicket as AttachTicketIcon,
  CrossedPlane,
} from '@icons/common';
import { ValueOf } from '@utils/tsUtils';
import { ORDER_STATUSES as STATUSES } from '../OrderStatus/constants';

export interface IOrderCard {
  className?: string;
  attachable?: boolean;
  detachable?: boolean;
  countryCode?: string;
  details?: string;
  hotelName: string;
  region?: string | null;
  segment?: string | null;
  href?: string;
  hasFlights?: boolean;
  id?: string;
  subAgentName?: string;
  onAttachTicket?: ({ orderId }: { orderId: IOrderCard['id'] }) => void;
  onClickOrderId?: (id: IOrderCard['id']) => void;
  status: ValueOf<typeof STATUSES>;

  // TODO: типизировать activeTicket и tickets после рефаакторинга и типизации стора ЦРМ (https://tracker.yandex.ru/LT-40742)
  activeTicket?: any;
  tickets?: any;
}

const OrderCard = ({
  className,
  attachable = false,
  detachable = false,
  countryCode = '',
  details = '',
  href = '',
  hotelName,
  hasFlights,
  id,
  region = '',
  segment,
  status,
  activeTicket = null,
  tickets,
  subAgentName,
  onClickOrderId = id => null,
  onAttachTicket = ({ orderId }) => null,
}: IOrderCard) => {
  const handleAttachTicket = useCallback(() => {
    onAttachTicket({ orderId: id });
  }, [onAttachTicket]);

  return (
    <StyledOrderCard className={className}>
      <StyledTopContainer>
        <StyledMainInfo>
          <StyledOrderStatus
            onClickId={onClickOrderId}
            href={href}
            id={id}
            status={status}
          />
          {!hasFlights && <StyledCrossedPlane />}
          {segment && <StyledSegment title={segment}>{segment}</StyledSegment>}
        </StyledMainInfo>
        <StyledControls>
          {(attachable || detachable) && (
            <StyledAttachTicketButton
              functional
              onClick={handleAttachTicket}
              title="Прикрепить тикет"
            >
              <StyledAttachTicketIcon detachable={detachable} />
            </StyledAttachTicketButton>
          )}
        </StyledControls>
      </StyledTopContainer>
      {!!subAgentName && (
        <StyledSubAgentName>{subAgentName}</StyledSubAgentName>
      )}
      <StyledHotelName title={hotelName}>{hotelName}</StyledHotelName>
      <StyledBottomContainer>
        {countryCode && <StyledCountryFlag country={countryCode} />}
        <StyledMiddleBottomContainer>
          <StyledRegion title={region || ''}>{region}</StyledRegion>
          <StyledDetails title={details}>{details}</StyledDetails>
        </StyledMiddleBottomContainer>
        {countryCode && <StyledCountryCode>{countryCode}</StyledCountryCode>}
      </StyledBottomContainer>
      {Array.isArray(tickets) &&
        tickets.map(({ ticket }) => {
          if (activeTicket?.id === ticket.id) return null;
          return (
            <StyledTicketLink key={ticket.id} href={`/tickets/${ticket.id}`}>
              <StyledTicketArrow />
              {`[${ticket.id}] ${ticket.subject}`}
            </StyledTicketLink>
          );
        })}
    </StyledOrderCard>
  );
};

const StyledOrderCard = styled.div`
  width: 100%;
`;

const StyledTicketLink = styled.a`
  display: inline-block;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  line-height: 16px;
  color: #007aff;
  z-index: 10;
`;

const StyledTicketArrow = styled(ArrowUpRight)`
  width: 9px;
  height: 8px;

  display: inline-block;
  margin-right: 4px;
`;

const StyledTopContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const StyledMainInfo = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledOrderStatus = styled(OrderStatus)`
  display: inline-block;
  min-width: 125px;
`;

const StyledSegment = styled.div`
  color: #4d4d4d;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${StyledOrderStatus} + & {
    margin-left: 10px;
  }
`;

const StyledControls = styled.div`
  display: flex;

  * + & {
    margin-left: 10px;
  }

  > * + * {
    margin-left: 10px;
  }
`;

const StyledAttachTicketButton = styled(Button)`
  height: 14px;
`;

const StyledAttachTicketIcon = styled(AttachTicketIcon)<{
  detachable: IOrderCard['detachable'];
}>`
  height: 100%;
  width: auto;
  ${({ detachable }) => detachable && 'transform: rotateX(180deg)'};
`;

const StyledHotelName = styled.div`
  color: #17181a;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledBottomContainer = styled.div`
  color: #919499;
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  font-size: 12px;
  justify-content: space-between;
  margin-top: 7px;
`;

const StyledCountryFlag = styled(Flag)`
  background-color: black;
  flex-shrink: 0;
  height: 12px;
  width: 16px;
`;

const StyledMiddleBottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: 0 7px;
  overflow: hidden;
`;

const StyledDetails = styled.div`
  color: #4d4d4d;
  font-size: 14px;
  height: 16px;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSubAgentName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #4b137b;
  margin: 3px 0 8px 0;
`;

const StyledCrossedPlane = styled(CrossedPlane)`
  width: 18px;
  height: 18px;
  margin: 0 5px;
`;

const StyledRegion = styled(StyledDetails)``;

const StyledCountryCode = styled.div``;

export default OrderCard;
